import {
	SkioldOneClient,
	FileResponse,
	IActiveSow as IActiveSowApi,
	ActiveSow as ActiveSowApi,
	IDadMomTableItem,
	DadMomTableItem,
	StemAnimal,
	YoungAnimalTableItem,
	IYoungAnimalTableItem,
	CellColorStyle,
	DepartedSow,
	IDepartedSow,
	IMatingEventPdfItem,
	MatingEventPdfItem,
	IScanningEventPdfItem,
	ScanningEventPdfItem,
	FarrowingEventPdfItem,
	IFarrowingEventPdfItem,
	IWeaningEventPdfItem,
	WeaningEventPdfItem,
	DeadPigletsEventPdfItem,
	IDeadPigletsEventPdfItem,
} from 'shared/api/api';
import { SkioldDigitalApiBaseUrl } from 'shared/constants';
import { savePdfAs } from './general-pdf-helper';
import { ActiveSow } from 'web/view/components/stem-animal/animal-lists/active-sows/active-sows-model';
import { getDateString } from 'shared/helpers/date-helpers';
import { getAnimalNumberColor } from 'shared/helpers/treatment-helper/treatment-helper';
import { localized } from 'shared/state/i18n/i18n';
import { calculateAgeInWeeks } from 'shared/helpers/general-helpers';
import * as XLSX from 'xlsx';
import { calculateLitter } from 'shared/helpers/stemanimal-helper/stemanimal-helper';

export function GetAnimalListPdf(
	activeSows: ActiveSow[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	let activeSowsApi: IActiveSowApi[] = [];
	activeSows.forEach(activeSow => {
		activeSowsApi.push({
			transponder: activeSow.transponder,
			animalNumber: activeSow.animalNumber,
			entranceType: activeSow.entranceType,
			entranceDate: getDateString(activeSow.entranceDate),
			race: activeSow.race,
			animalKind: localized(activeSow.kind ?? ''),
			batch: activeSow.batch,
			cycleDays: activeSow.cycleDays ? activeSow.cycleDays.toString() : '',
			condition: activeSow.condition,
			teats: activeSow.teats ? activeSow.teats.toString() : '',
			litterNumber: activeSow.litterNumber,
			cellColorStyle: getAnimalNumberColor(activeSow.id),
		});
	});

	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_Post(activeSowsApi as ActiveSowApi[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetDadMomTablePdf(
	activeSows: ActiveSow[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	let activeSowsApi: IDadMomTableItem[] = [];
	activeSows.forEach(activeSow => {
		activeSowsApi.push({
			animalNumber: activeSow.animalNumber,
			birthDate: getDateString(activeSow.birthDate),
			race: activeSow.race,
			dadIdNumber: activeSow.dadIdNumber,
			dadNr: activeSow.dadAnimalNumber,
			idNumber: activeSow.idNumber,
			index: activeSow.breedIndex?.toString(),
			momIdNumber: activeSow.momIdNumber,
			momNr: activeSow.momAnimalNumber,
			raceLine: activeSow.raceLine,
			cellColorStyle: getAnimalNumberColor(activeSow.id),
		});
	});

	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PPrintDadMomTableListst(activeSowsApi as DadMomTableItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}
export function GetYoungAnimalPdf(
	youngAnimal: StemAnimal[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	let youngAnimalTableItem: IYoungAnimalTableItem[] = [];
	youngAnimal.forEach(y => {
		youngAnimalTableItem.push({
			idNumber: y.idNumber,
			birthDate: getDateString(y.birthDate),
			race: y.race,
			gender: y.gender,
			dadIdNumber: y.dadIdNumber,
			dadNr: y.dadAnimalNumber,
			index: y.breedIndex?.toString(),
			momIdNumber: y.momIdNumber,
			momNr: y.momAnimalNumber,
			raceLine: y.raceLine,
			cellColorStyle: CellColorStyle.Normal,
			age: y.birthDate ? calculateAgeInWeeks(y.birthDate).toString() : '',
		});
	});

	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_Post2(youngAnimalTableItem as ActiveSowApi[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetDepartedSowsPdf(
	departedSows: IDepartedSow[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintDepartedSowList(departedSows as DepartedSow[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetMatingEventPdf(
	matings: IMatingEventPdfItem[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintMatingList(matings as MatingEventPdfItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetScanningEventPdf(
	scannings: IScanningEventPdfItem[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintScanningList(scannings as ScanningEventPdfItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetFarrowingEventPdf(
	farrowings: IFarrowingEventPdfItem[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintFarrowingList(farrowings as FarrowingEventPdfItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetWeaningEventPdf(
	weanings: IWeaningEventPdfItem[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintWeaningList(weanings as WeaningEventPdfItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

export function GetDeadPigletsEventPdf(
	weanings: IDeadPigletsEventPdfItem[],
	fileName: string,
	siteId: string,
	timeZoneId?: string,
	lang?: string,
) {
	return new SkioldOneClient(SkioldDigitalApiBaseUrl)
		.animalListReport_PrintDeadPigletsList(weanings as DeadPigletsEventPdfItem[], siteId, timeZoneId, lang)
		.then((fileResponse: FileResponse | null) => {
			savePdfAs(fileResponse, fileName);
		})
		.catch(() => {
			return;
		});
}

/**
 * Converts an array of IMatingEventPdfItem to an Excel file and triggers a download in the browser.
 * @param matings Array of IMatingEventPdfItem
 * @param fileName Name of the Excel file to save (e.g., 'mating-events.xlsx')
 */
export function exportMatingEventsToExcel(matings: IMatingEventPdfItem[], fileName: string) {
	const worksheetData = [
		[
			localized('animalNumber'),
			localized('Race'),
			localized('LitterNr'),
			localized('Date'),
			localized('boar'),
			localized('Grade'),
			localized('InitialsShort'),
		],
		...matings.map(item => [
			item.animalNumber ?? '',
			item.race ?? '',
			item.litter ?? '',
			item.registrationDate ?? '',
			item.boarNumber ?? '',
			item.grade ?? '',
			item.initials ?? '',
			item.cellColorStyle ?? '',
		]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Mating Events');
	XLSX.writeFile(workbook, fileName);
}

/**
 * Converts an array of IScanningEventPdfItem to an Excel file and triggers a download in the browser.
 * @param scannings Array of IScanningEventPdfItem
 * @param fileName Name of the Excel file to save (e.g., 'scanning-events.xlsx')
 */
export function exportScanningEventsToExcel(scannings: IScanningEventPdfItem[], fileName: string) {
	const worksheetData = [
		[
			localized('animalNumber'),
			localized('Race'),
			localized('LitterNr'),
			localized('Date'),
			localized('Pregnant'),
			localized('InitialsShort'),
		],
		...scannings.map(item => [
			item.animalNumber ?? '',
			item.race ?? '',
			item.litter ?? '',
			item.registrationDate ?? '',
			item.scanResult ?? '',
			item.initials ?? '',
		]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Scanning Events');
	XLSX.writeFile(workbook, fileName);
}

/**
 * Converts an array of IFarrowingEventPdfItem to an Excel file and triggers a download in the browser.
 * @param farrowings Array of IFarrowingEventPdfItem
 * @param fileName Name of the Excel file to save (e.g., 'farrowing-events.xlsx')
 */
export function exportFarrowingEventsToExcel(farrowings: IFarrowingEventPdfItem[], fileName: string) {
	const worksheetData = [
		[
			localized('animalNumber'),
			localized('Race'),
			localized('Location'),
			localized('LitterNr'),
			localized('Date'),
			localized('alive'),
			localized('dead'),
			localized('Abortion'),
			localized('InitialsShort'),
		],
		...farrowings.map(item => [
			item.animalNumber ?? '',
			item.race ?? '',
			item.location ?? '',
			item.litter ?? '',
			item.registrationDate ?? '',
			item.alive ?? '',
			item.dead ?? '',
			item.abortion ?? '',
			item.initials ?? '',
		]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Farrowing Events');
	XLSX.writeFile(workbook, fileName);
}

/**
 * Converts an array of IWeaningEventPdfItem to an Excel file and triggers a download in the browser.
 * @param weanings Array of IWeaningEventPdfItem
 * @param fileName Name of the Excel file to save (e.g., 'weaning-events.xlsx')
 */
export function exportWeaningEventsToExcel(weanings: IWeaningEventPdfItem[], fileName: string) {
	const worksheetData = [
		[
			localized('animalNumber'),
			localized('Race'),
			localized('LitterNr'),
			localized('Date'),
			localized('pcsWithoutDot'),
			localized('AvgWeight'),
			localized('nursingSow'),
			localized('InitialsShort'),
		],
		...weanings.map(item => [
			item.animalNumber ?? '',
			item.race ?? '',
			item.litter ?? '',
			item.registrationDate ?? '',
			item.weanedPcs ?? '',
			item.avgWeight ?? '',
			item.nursingSow ?? '',
			item.initials ?? '',
		]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Weaning Events');
	XLSX.writeFile(workbook, fileName);
}

export function exportDeadPigletsEventsToExcel(weanings: IDeadPigletsEventPdfItem[], fileName: string) {
	const worksheetData = [
		[
			localized('animalNumber'),
			localized('Race'),
			localized('LitterNr'),
			localized('Batch'),
			localized('Date'),
			localized('dead'),
			localized('departureType'),
			localized('reason'),
			localized('InitialsShort'),
		],
		...weanings.map(item => [
			item.animalNumber ?? '',
			item.race ?? '',
			item.litter ?? '',
			item.matingBatch ?? '',
			item.registrationDate ?? '',
			item.deadPiglets ?? '',
			item.departureType ?? '',
			item.departureReason ?? '',
			item.initials ?? '',
		]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Dead Piglets Events');
	XLSX.writeFile(workbook, fileName);
}
